import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { doc, getDoc, setDoc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";

// Course data based on user type
const sellerCourses = [
  { 
    id: "solar-design",
    title: "Solar Design & Installation Mastery", 
    desc: "Master the art of designing and installing solar systems specifically for the Nigerian market. This comprehensive course covers everything from initial site assessment to final commissioning.",
    duration: "4 weeks",
    level: "Intermediate",
    modules: [
      "Site Assessment & Solar Resource Analysis",
      "System Sizing & Component Selection", 
      "Electrical Design & Safety Standards",
      "Installation Best Practices & Quality Control"
    ],
    learningOutcomes: [
      "Design solar systems for residential and commercial applications",
      "Calculate accurate system sizing based on Nigerian weather patterns",
      "Ensure compliance with local electrical codes and safety standards",
      "Implement quality control measures for long-term system reliability"
    ],
    detailedDescription: `This intensive 4-week program is designed for solar professionals who want to excel in the Nigerian market. You'll learn how to assess sites for optimal solar potential, design systems that maximize energy production, and install them to the highest standards of safety and reliability.

The course includes hands-on training with real-world case studies from Nigerian installations, covering both grid-tied and off-grid systems. You'll master the technical aspects of solar design while understanding the unique challenges and opportunities in the Nigerian energy market.

By the end of this course, you'll be able to confidently design and install solar systems that not only meet your clients' energy needs but also provide excellent return on investment and long-term reliability.`
  },
  { 
    id: "sales-marketing",
    title: "Solar Sales & Marketing Excellence", 
    desc: "Transform your solar business with proven sales strategies, digital marketing techniques, and customer relationship management tailored for the Nigerian market.",
    duration: "3 weeks",
    level: "Beginner",
    modules: [
      "Understanding the Nigerian Solar Market",
      "Sales Psychology & Customer Needs Analysis",
      "Digital Marketing & Social Media Strategy",
      "Customer Relationship Management & Retention"
    ],
    learningOutcomes: [
      "Develop effective sales strategies for the Nigerian solar market",
      "Create compelling marketing campaigns that resonate with local customers",
      "Build and maintain strong customer relationships",
      "Increase conversion rates and customer lifetime value"
    ],
    detailedDescription: `The Nigerian solar market is growing rapidly, but success requires more than just technical expertise. This course teaches you how to effectively communicate the value of solar energy to Nigerian customers, build trust, and close deals consistently.

You'll learn about the unique characteristics of the Nigerian market, including customer preferences, purchasing behaviors, and decision-making processes. The course covers both traditional sales techniques and modern digital marketing strategies, helping you reach customers through multiple channels.

From understanding customer pain points to creating compelling proposals and following up effectively, this course provides the complete toolkit for solar sales success in Nigeria.`
  },
  { 
    id: "maintenance",
    title: "Solar System Maintenance & Troubleshooting", 
    desc: "Become an expert in maintaining solar installations and quickly resolving technical issues to ensure optimal system performance and customer satisfaction.",
    duration: "2 weeks",
    level: "Intermediate",
    modules: [
      "Preventive Maintenance Procedures",
      "Performance Monitoring & Analysis",
      "Common Issues & Troubleshooting Techniques",
      "Emergency Response & Safety Protocols"
    ],
    learningOutcomes: [
      "Implement comprehensive maintenance schedules for solar systems",
      "Diagnose and resolve common technical issues efficiently",
      "Monitor system performance and identify optimization opportunities",
      "Handle emergency situations safely and professionally"
    ],
    detailedDescription: `Solar systems require regular maintenance to perform at their best and provide maximum value to customers. This course teaches you how to establish and execute effective maintenance programs that prevent problems before they occur and quickly resolve issues when they arise.

You'll learn how to use monitoring tools to track system performance, identify potential problems early, and optimize energy production. The course covers both routine maintenance procedures and advanced troubleshooting techniques, ensuring you can handle any situation professionally.

With hands-on training in real maintenance scenarios, you'll develop the skills and confidence to provide excellent after-sales service that builds customer loyalty and generates repeat business.`
  },
  { 
    id: "business-management",
    title: "Solar Business Management & Growth", 
    desc: "Learn essential business management skills to scale your solar company, manage finances, lead teams, and build a sustainable competitive advantage.",
    duration: "3 weeks",
    level: "Beginner",
    modules: [
      "Business Planning & Strategy Development",
      "Financial Management & Cash Flow Optimization",
      "Team Leadership & Project Management",
      "Scaling Operations & Market Expansion"
    ],
    learningOutcomes: [
      "Develop comprehensive business strategies for solar company growth",
      "Manage finances effectively and optimize cash flow",
      "Lead teams and manage projects efficiently",
      "Scale operations while maintaining quality and profitability"
    ],
    detailedDescription: `Running a successful solar business requires more than technical skills. This course provides the essential business management knowledge you need to grow your company sustainably and profitably in the competitive Nigerian market.

You'll learn how to develop strategic business plans, manage finances effectively, lead teams, and scale operations while maintaining quality standards. The course covers everything from basic business operations to advanced growth strategies.

Whether you're just starting your solar business or looking to scale existing operations, this course provides the practical tools and insights you need to build a successful, sustainable company in the renewable energy sector.`
  }
];

const buyerCourses = [
  { 
    id: "solar-basics",
    title: "Solar Energy Fundamentals", 
    desc: "Master the fundamentals of solar energy technology and understand how it can benefit your home or business in Nigeria's unique energy landscape.",
    duration: "2 weeks",
    level: "Beginner",
    modules: [
      "How Solar Energy Works",
      "Solar Technology & Components",
      "Nigerian Energy Landscape & Solar Potential",
      "Environmental & Economic Benefits"
    ],
    learningOutcomes: [
      "Understand the science behind solar energy generation",
      "Identify the key components of solar systems",
      "Recognize Nigeria's solar potential and energy challenges",
      "Evaluate the benefits of solar adoption for your situation"
    ],
    detailedDescription: `Solar energy is transforming how we power our homes and businesses, but understanding how it works is crucial for making informed decisions. This comprehensive course breaks down the complex world of solar energy into easy-to-understand concepts, specifically tailored for the Nigerian context.

You'll learn about the science behind solar panels, how they convert sunlight into electricity, and why Nigeria is particularly well-suited for solar energy adoption. The course explores the country's abundant solar resources and how they can help address energy challenges while providing significant cost savings.

By understanding the fundamentals, you'll be better equipped to evaluate solar options, ask the right questions, and make confident decisions about solar adoption for your home or business.`
  },
  { 
    id: "system-selection",
    title: "Choosing Your Perfect Solar System", 
    desc: "Navigate the complex process of selecting the right solar system for your specific needs, budget, and location in Nigeria.",
    duration: "2 weeks",
    level: "Beginner",
    modules: [
      "Assessing Your Energy Needs & Consumption",
      "System Types: Grid-Tied vs Off-Grid vs Hybrid",
      "Component Selection & Quality Considerations",
      "Cost-Benefit Analysis & ROI Calculations"
    ],
    learningOutcomes: [
      "Accurately assess your energy consumption and solar requirements",
      "Choose between different system types based on your situation",
      "Evaluate component quality and manufacturer reliability",
      "Calculate return on investment and long-term cost savings"
    ],
    detailedDescription: `Choosing the right solar system is one of the most important decisions you'll make, and it's not just about picking the cheapest option. This course guides you through the entire selection process, helping you understand your energy needs, evaluate different system types, and make informed decisions that maximize your investment.

You'll learn how to analyze your current energy consumption, determine the optimal system size, and choose between grid-tied, off-grid, and hybrid systems based on your location and requirements. The course also covers how to evaluate component quality, understand warranties, and calculate the true cost and benefits of solar adoption.

With practical tools and real-world examples from Nigerian installations, you'll be able to confidently navigate the solar market and select a system that provides the best value for your specific situation.`
  },
  { 
    id: "financing-options",
    title: "Solar Financing & Investment Strategies", 
    desc: "Explore the various financing options available for solar adoption in Nigeria and learn how to make solar energy affordable for your budget.",
    duration: "1 week",
    level: "Beginner",
    modules: [
      "Understanding Solar Investment Costs",
      "Financing Options: Loans, Leases & PPAs",
      "Government Incentives & Tax Benefits",
      "ROI Analysis & Payback Period Calculations"
    ],
    learningOutcomes: [
      "Understand the true costs and benefits of solar investment",
      "Evaluate different financing options and their implications",
      "Identify available government incentives and tax benefits",
      "Calculate realistic payback periods and return on investment"
    ],
    detailedDescription: `Solar energy is a significant investment, but there are many ways to make it affordable and financially beneficial. This course explores the various financing options available in Nigeria, from traditional loans to innovative payment plans, helping you find the best way to fund your solar project.

You'll learn about the different types of financing available, including bank loans, solar leases, power purchase agreements (PPAs), and government programs. The course also covers how to calculate the true cost of solar energy, including maintenance and replacement costs, and how to determine realistic payback periods.

With practical examples and case studies from Nigerian solar projects, you'll understand how to structure your solar investment to maximize financial benefits while minimizing upfront costs.`
  },
  { 
    id: "maintenance-buyer",
    title: "Solar System Care & Maintenance", 
    desc: "Learn how to properly maintain your solar system to ensure optimal performance, longevity, and maximum return on your investment.",
    duration: "1 week",
    level: "Beginner",
    modules: [
      "Understanding System Performance & Monitoring",
      "Routine Maintenance Tasks & Schedules",
      "Identifying Common Issues & When to Call Professionals",
      "Maximizing System Lifespan & Performance"
    ],
    learningOutcomes: [
      "Monitor your solar system's performance effectively",
      "Perform routine maintenance tasks safely",
      "Identify potential problems before they become serious",
      "Maximize system lifespan and energy production"
    ],
    detailedDescription: `Your solar system is a significant investment, and proper maintenance is essential to protect that investment and ensure it performs at its best for decades. This course teaches you everything you need to know about caring for your solar system, from routine maintenance to identifying when you need professional help.

You'll learn how to monitor your system's performance, understand what normal operation looks like, and identify potential issues early. The course covers both DIY maintenance tasks that you can safely perform yourself and situations where professional assistance is required.

With proper maintenance, your solar system can provide reliable, clean energy for 25 years or more. This course gives you the knowledge and confidence to protect your investment and maximize your solar energy benefits.`
  }
];

const Academy = () => {
  const { currentUser, userProfile } = useAuth();
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);

  // Get appropriate courses based on user type
  const courses = userProfile?.userType === 'seller' ? sellerCourses : buyerCourses;

  useEffect(() => {
    const loadEnrolledCourses = async () => {
      if (!currentUser) return;
      
      try {
        setLoading(true);
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        
        if (userDoc.exists()) {
          const data = userDoc.data();
          setEnrolledCourses(data.enrolledCourses || []);
        }
      } catch (error) {
        console.error('Error loading enrolled courses:', error);
      } finally {
        setLoading(false);
      }
    };

    loadEnrolledCourses();
  }, [currentUser]);

  const handleEnroll = async (courseId) => {
    if (!currentUser) return;
    
    try {
      setEnrolling(true);
      const newEnrolledCourses = [...enrolledCourses, courseId];
      
      await updateDoc(doc(db, 'users', currentUser.uid), {
        enrolledCourses: newEnrolledCourses,
        updatedAt: new Date().toISOString()
      });
      
      setEnrolledCourses(newEnrolledCourses);
    } catch (error) {
      console.error('Error enrolling in course:', error);
      alert('Error enrolling in course. Please try again.');
    } finally {
      setEnrolling(false);
    }
  };

  const isEnrolled = (courseId) => enrolledCourses.includes(courseId);

  if (loading) {
    return (
      <div className="bg-abiu-green-bg min-h-screen py-10 px-4">
        <div className="max-w-4xl mx-auto w-full flex items-center justify-center">
          <div className="w-8 h-8 border-4 border-abiu-green border-t-transparent rounded-full animate-spin"></div>
          <span className="ml-3 text-gray-600">Loading courses...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-abiu-green-bg min-h-screen py-10 px-4">
      <main className="max-w-4xl mx-auto w-full">
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-abiu-green-dark mb-2">Skills Academy</h1>
              <p className="text-gray-700 text-sm sm:text-base">
                {userProfile?.userType === 'seller' 
                  ? 'Professional courses for solar business owners and installers'
                  : 'Learn about solar energy and make informed decisions'
                }
              </p>
            </div>
            {userProfile?.userType && (
              <div className="sm:text-right">
                <div className={`inline-flex items-center px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-bold ${
                  userProfile.userType === 'buyer' 
                    ? 'bg-blue-100 text-blue-800 border-2 border-blue-300' 
                    : 'bg-green-100 text-green-800 border-2 border-green-300'
                }`}>
                  <span className="mr-1 sm:mr-2">
                    {userProfile.userType === 'buyer' ? '🛒' : '🏢'}
                  </span>
                  <span className="hidden sm:inline">{userProfile.userType === 'buyer' ? 'BUYER' : 'SELLER'} COURSES</span>
                  <span className="sm:hidden">{userProfile.userType === 'buyer' ? 'BUYER' : 'SELLER'}</span>
                </div>
              </div>
            )}
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow p-4 sm:p-8 mb-10 w-full">
          <div className="font-semibold text-gray-800 mb-4 sm:mb-6 text-base sm:text-lg">Available Courses</div>
          <div className="grid gap-4 sm:gap-6">
            {courses.map((course) => (
              <div key={course.id} className="border border-gray-200 rounded-lg p-4 sm:p-6 hover:shadow-md transition-shadow">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex-1">
                    <div className="font-medium text-abiu-green-dark text-base sm:text-lg mb-2">{course.title}</div>
                    <div className="text-gray-600 text-xs sm:text-sm mb-3">{course.desc}</div>
                    <div className="flex flex-wrap gap-2 sm:gap-4 text-xs text-gray-500">
                      <span>⏱️ {course.duration}</span>
                      <span>📊 {course.level}</span>
                    </div>
                  </div>
                  <div className="mt-4 sm:mt-0 flex flex-col sm:flex-row gap-2">
                    {isEnrolled(course.id) ? (
                      <>
                        <Link 
                          to={`/academy/course/${course.id}`}
                          className="bg-abiu-green text-white px-4 py-2 rounded font-medium hover:bg-abiu-green-dark transition text-center text-sm"
                        >
                          View Course
                        </Link>
                        <span className="text-abiu-green-dark font-semibold text-xs sm:text-sm flex items-center justify-center">
                          ✓ Enrolled
                        </span>
                      </>
                    ) : (
                      <button 
                        onClick={() => handleEnroll(course.id)}
                        disabled={enrolling}
                        className="bg-abiu-green text-white px-4 sm:px-6 py-2 rounded font-medium hover:bg-abiu-green-dark transition disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                      >
                        {enrolling ? 'Enrolling...' : 'Enroll'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        <Link to="/marketplace" className="text-abiu-green hover:underline">&larr; Back to Marketplace</Link>
      </main>
    </div>
  );
};

export default Academy; 